import Decimal from 'decimal.js';
import { z } from 'zod';

const integerNumberBrand = 'number/integer';
const decimalNumberBrand = 'number/decimal';

export type IntegerNumber = z.BRAND<typeof integerNumberBrand>;
export type DecimalNumber = z.BRAND<typeof decimalNumberBrand>;

export type DecimalPlaces = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 9 | 10;
type ScaleFactors = 0 | 2 | 8 | 10;

export type DecimalAmount<TDecimalPlaces extends number = number> = DecimalNumber &
  (TDecimalPlaces | number) &
  z.BRAND<`currency/decimal_amount/${TDecimalPlaces}`>;

export type IntegerAmount<TScaleFactor extends number = number> = IntegerNumber &
  (TScaleFactor | number) &
  z.BRAND<`currency/integer_amount/${TScaleFactor}`>;

const zni = z.number().brand(integerNumberBrand);
const znd = z.number().brand(decimalNumberBrand);
export const zodIntegerAmount = zni.brand<IntegerAmount>();
export const zodIntegerAmount0 = zni.brand<'currency/integer_amount/0'>();
export const zodIntegerAmount2 = zni.brand<'currency/integer_amount/2'>();
export const zodIntegerAmount3 = zni.brand<'currency/integer_amount/3'>();
export const zodIntegerAmount8 = zni.brand<'currency/integer_amount/8'>();
export const zodIntegerAmount10 = zni.brand<'currency/integer_amount/10'>();

export const zodDecimalAmount = znd.brand<DecimalAmount>();
export const zodDecimalAmount0 = znd.brand<'currency/decimal_amount/0'>();
export const zodDecimalAmount1 = znd.brand<'currency/decimal_amount/1'>();
export const zodDecimalAmount2 = znd.brand<'currency/decimal_amount/2'>();
export const zodDecimalAmount3 = znd.brand<'currency/decimal_amount/3'>();
export const zodDecimalAmount4 = znd.brand<'currency/decimal_amount/4'>();
export const zodDecimalAmount5 = znd.brand<'currency/decimal_amount/5'>();
export const zodDecimalAmount6 = znd.brand<'currency/decimal_amount/6'>();
export const zodDecimalAmount9 = znd.brand<'currency/decimal_amount/9'>();
export const zodDecimalAmount10 = znd.brand<'currency/decimal_amount/10'>();

export function makeDecimalAmountStrict<V extends number, TDecimalPlaces extends DecimalPlaces = 2>(
  value: V &
    (V extends DecimalAmount<TDecimalPlaces>
      ? { __ERROR: 'redundant call of `makeDecimalAmountStrict(DecimalAmount)`' }
      : number),
  decimalPlaces: TDecimalPlaces = 2 as TDecimalPlaces,
): DecimalAmount<TDecimalPlaces> {
  return makeDecimalAmountStrictInternal(value, decimalPlaces);
}

export function tryMakeDecimalAmountStrict<
  V extends number,
  TDecimalPlaces extends DecimalPlaces = 2,
>(
  value: V &
    (V extends DecimalAmount<TDecimalPlaces>
      ? { __ERROR: 'redundant call of `tryMakeDecimalAmountStrict(DecimalAmount)`' }
      : number),
  expectedDecimalPlaces: TDecimalPlaces = 2 as TDecimalPlaces,
): { success: true; value: DecimalAmount<TDecimalPlaces> } | { success: false; value: undefined } {
  try {
    return {
      success: true,
      value: makeDecimalAmountStrictInternal(value, expectedDecimalPlaces),
    };
  } catch {
    return { success: false, value: undefined };
  }
}

function makeDecimalAmountStrictInternal<
  V extends number,
  TDecimalPlaces extends DecimalPlaces = 2,
>(
  value: V,
  expectedDecimalPlaces: TDecimalPlaces = 2 as TDecimalPlaces,
): DecimalAmount<TDecimalPlaces> {
  const decimal = new Decimal(value);

  if (!decimal.isFinite()) {
    throw Object.assign(new Error('Invalid decimal amount'), { context: { value } });
  }

  const decimalPlaces = decimal.decimalPlaces();

  if (decimalPlaces > expectedDecimalPlaces) {
    throw Object.assign(new Error('Unexpected decimal places'), {
      context: { value, decimalPlaces, expectedDecimalPlaces },
    });
  }

  return makeDecimalAmountInternal(value, expectedDecimalPlaces);
}

export function makeDecimalAmount<V extends number, TDecimalPlaces extends DecimalPlaces = 2>(
  value: V &
    (V extends DecimalAmount<TDecimalPlaces>
      ? { __ERROR: 'redundant call of `makeDecimalAmount(DecimalAmount)`' }
      : number),
  decimalPlaces: TDecimalPlaces = 2 as TDecimalPlaces,
): DecimalAmount<TDecimalPlaces> {
  return makeDecimalAmountInternal(value, decimalPlaces);
}

function makeDecimalAmountInternal<V extends number, TDecimalPlaces extends DecimalPlaces = 2>(
  value: V,
  decimalPlaces: TDecimalPlaces = 2 as TDecimalPlaces,
): DecimalAmount<TDecimalPlaces> {
  switch (decimalPlaces) {
    case 0 as const:
      return zodDecimalAmount0.parse(value) as unknown as DecimalAmount<TDecimalPlaces>;
    case 1 as const:
      return zodDecimalAmount1.parse(value) as unknown as DecimalAmount<TDecimalPlaces>;
    case 2 as const:
      return zodDecimalAmount2.parse(value) as unknown as DecimalAmount<TDecimalPlaces>;
    case 3 as const:
      return zodDecimalAmount3.parse(value) as unknown as DecimalAmount<TDecimalPlaces>;
    case 4 as const:
      return zodDecimalAmount4.parse(value) as unknown as DecimalAmount<TDecimalPlaces>;
    case 5 as const:
      return zodDecimalAmount5.parse(value) as unknown as DecimalAmount<TDecimalPlaces>;
    case 6 as const:
      return zodDecimalAmount6.parse(value) as unknown as DecimalAmount<TDecimalPlaces>;
    case 9 as const:
      return zodDecimalAmount9.parse(value) as unknown as DecimalAmount<TDecimalPlaces>;
    case 10 as const:
      return zodDecimalAmount10.parse(value) as unknown as DecimalAmount<TDecimalPlaces>;
    default:
      return exhaustive(decimalPlaces);
  }
}

export function makeIntegerAmount<V extends number, TScaleFactor extends ScaleFactors = 2>(
  value: V extends IntegerAmount<TScaleFactor>
    ? { __ERROR: 'redundant call of `makeIntegerAmount(IntegerAmount)`' }
    : number,
  scaleFactor: TScaleFactor = 2 as TScaleFactor,
): IntegerAmount<TScaleFactor> {
  switch (scaleFactor) {
    case 0 as const:
      return zodIntegerAmount0.parse(value) as unknown as IntegerAmount<TScaleFactor>;
    case 2 as const:
      return zodIntegerAmount2.parse(value) as unknown as IntegerAmount<TScaleFactor>;
    case 8 as const:
      return zodIntegerAmount8.parse(value) as unknown as IntegerAmount<TScaleFactor>;
    case 10 as const:
      return zodIntegerAmount10.parse(value) as unknown as IntegerAmount<TScaleFactor>;
    default:
      return exhaustive(scaleFactor);
  }
}

function exhaustive(_: never): never {
  throw new Error('Failed exhaustive check');
}
